import { DimensionId } from "@/types/questionnaries";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { buildAnswerByResponseIdQueryKey } from "./answer-by-response-id.hook";
import { buildResponseByIdQueryKey } from "./response-by-id.hook";
import { useUpdateAdditionalCommentsMutation } from "./update-additional-comments.hook";
import { useUpdateQuizAnswer } from "./update-quiz-answer.hook";

export type RemoveQuestionAnswersMutationVariables = {
  companyId: string;
  quizId: string;
  questionId: string;
  responseId: string;
  dimension: DimensionId;
  topic: string;
  questionAllowsAdditionalComments: boolean;
  invalidateQuestionResponses: boolean;
};

export const useRemoveQuestionAnswersMutation = () => {
  const queryClient = useQueryClient();

  const updateAnswersMutation = useUpdateQuizAnswer();

  const additionalCommentsMutation = useUpdateAdditionalCommentsMutation();

  return useMutation({
    mutationFn: (variables: RemoveQuestionAnswersMutationVariables) => {
      return Promise.allSettled([
        updateAnswersMutation.mutateAsync({
          companyId: variables.companyId,
          quizId: variables.quizId,
          questionId: variables.questionId,
          action: "REMOVE",
          dimension: variables.dimension,
          topic: variables.topic,
        }),
        // Set a timeout to avoid race conditions (already checked)
        variables.questionAllowsAdditionalComments &&
          new Promise((resolve) => setTimeout(resolve, 1000)).then(() =>
            additionalCommentsMutation.mutateAsync({
              companyId: variables.companyId,
              quizId: variables.quizId,
              responseId: variables.responseId,
              additionalComments: "",
              dimension: variables.dimension,
              topic: variables.topic,
            }),
          ),
      ]);
    },
    onError: () => {
      toast.error("No se pudo borrar las respuestas");
    },
    onSettled: (_, __, variables) => {
      if (!variables.invalidateQuestionResponses) {
        return;
      }

      // Invalidate the response
      // Possibly deprecated and not needed anymore (sorry no time to check)
      queryClient.refetchQueries({
        queryKey: [
          "companies",
          variables.companyId,
          "quiz",
          variables.quizId,
          "responses",
          {
            dimension: variables.dimension,
            topic: variables.topic,
          },
        ],
      });

      const responseQueryKey = buildResponseByIdQueryKey({
        companyId: variables.companyId,
        quizId: variables.quizId,
        responseId: variables.responseId,
      });

      queryClient.refetchQueries({
        queryKey: responseQueryKey,
      });

      const answerQueryKey = buildAnswerByResponseIdQueryKey({
        companyId: variables.companyId,
        quizId: variables.quizId,
        responseId: variables.responseId,
      });

      queryClient.refetchQueries({
        queryKey: answerQueryKey,
      });
    },
  });
};
